import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export enum ToastType {
  Success = 'success',
  Error = 'error',
  Info = 'info',
  Skipped = 'Skipped',
}

interface ToastProps {
  message: string;
  type?: ToastType;
  onClose: () => void;
  duration?: number;
}

export function CustomToast({
  message,
  type = ToastType.Success,
  onClose,
  duration = 3000,
}: ToastProps) {
  const { t } = useTranslation(['common']);
  const [isClosing, setIsClosing] = useState(false);

  const getTranslatedType = (type: ToastType) => {
    switch (type) {
      case ToastType.Success:
        return t('success');
      case ToastType.Error:
        return t('error');
      case ToastType.Info:
        return t('info');
      default:
        return t('info');
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsClosing(true);
      setTimeout(onClose, 300); // Wait for the fade-out animation to complete (300ms)
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getIcon = (type: ToastType) => {
    switch (type) {
      case ToastType.Success:
        return <CheckCircleIcon className="h-8 w-8 text-successDefault" />;
      case ToastType.Error:
        return <ExclamationTriangleIcon className="h-8 w-8 text-btnColor" />;
      case ToastType.Info:
        return <InformationCircleIcon className="h-8 w-8 text-secondarySemiDark" />;
      default:
        return <InformationCircleIcon className="h-8 w-8 text-secondarySemiDark" />;
    }
  };

  // Determine text color based on type
  const getTextColorClass = (type: ToastType) => {
    switch (type) {
      case ToastType.Success:
        return 'text-successDefault';
      case ToastType.Error:
        return 'text-btnColor';
      case ToastType.Info:
        return 'text-secondarySemiDark';
      default:
        return 'text-warningDefault';
    }
  };

  // Determine border color based on type
  const getBorderColorClass = (type: ToastType) => {
    switch (type) {
      case ToastType.Success:
        return 'border-successDefault';
      case ToastType.Error:
        return 'border-btnColor';
      case ToastType.Info:
        return 'border-secondarySemiDark';
      default:
        return 'border-warningDefault';
    }
  };

  return (
    <div
      className={`fixed bottom-4 right-20 z-[1000] min-w-60 max-w-96 overflow-hidden rounded-2xl border border-l-4 bg-white ${getBorderColorClass(
        type
      )} ${isClosing ? 'animate-toast-fade-out-up' : 'animate-toast-slide-in'}`}
    >
      <div className={`shadow-[ 0px 4px 8px 0px #0000001A] rounded-lg bg-white p-3 shadow`}>
        <div className="flex items-center space-x-2">
          {/* Icon */}
          <div className="flex items-center">{getIcon(type)}</div>
          <div>
            <h5 className={`!font-medium capitalize ${getTextColorClass(type)}`}>
              {getTranslatedType(type)}!
            </h5>
            <p className={`bodyXs flex-grow text-neutralDark`}>{message}</p>
          </div>
          {/* Close Button */}
          {/* <button
            onClick={onClose}
            className={`rounded-full p-1 ${getTextColorClass(type)} hover:bg-neutralLightmedium hover:bg-opacity-10`}
          >
            <XMarkIcon className="h-6 w-6" />
          </button> */}
        </div>
      </div>
    </div>
  );
}
